import React, { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';
import { useTranslation } from "react-i18next";

const ScatterChart = ({ data, selectedTrip, tripRte, tripDir, tripStart, inbound }) => {

  const [t] = useTranslation('common');

  const chartRef = useRef();
  const [refreshKey, setRefreshKey] = useState(0);

  let in_label=inbound;

  useEffect(() => {
    let sorted = data;

    // Time points in seconds array for conversion to date
    let realPointsY = sorted.map((e) => {
      return e.real_stop_time
    })

    // Unique shape dist traveled for x Scale domain
    let plannedData = [];
    let unique_sdt = [...new Set(sorted.map(i => i.shape_dist_traveled))];
    unique_sdt.forEach((e, i) => {
      let result = sorted.filter((f) => { return f.shape_dist_traveled == e });
      plannedData.push(result[0])
    })

    // Format planned data for multiple series scatter chart
    // PLANNED DATA IS TAKEN FROM REALTIME DATA (ONTIME) 
    // ON TIME TABLE STORES REAL STOP TIME AND PLANNED STOP TIME
    // IT IS THE ONLY WAY TO KEEP PLANNED DATA WHEN REFRESHING THE STATIC GTFS DATA
    let plannedPoints = plannedData.map((e) => {
      return {
        real_stop_time: e.planned_stop_time,
        stop_name: e.stop_name,
        shape_dist_traveled: e.shape_dist_traveled,
        status: e.status,
        diff: e.diff,
        date: 'Planifié',
        timepoint: e.timepoint
      }
    })

    // Tick labels (text)
    let tickLabels = plannedPoints.map((e) => { return e.stop_name });
    let tickLabelsFormat = [tickLabels[0], tickLabels[tickLabels.length - 1]];

    // Chart data : first value is drawn first, so second value is drawn over 1st value of this array
    let chartData = [
      { name: 'real', values: sorted },
      { name: 'planned', values: plannedPoints }
    ]

    let timeFormat = d3.timeFormat('%H:%M'); // - defining the format

    //Add time to y axis so late and early points still show
    realPointsY.push(Math.min(...realPointsY) - 900);
    realPointsY.push(Math.max(...realPointsY) + 900);
    //Convert seconds to dates and account for date after midnight
    let realTimes = realPointsY.map(i => new Date(i * 1000).toISOString().substring(11, 19));
    let parsedDateTimes = realTimes.map(d => {
      if (d.substring(0, 2) == '00' || d.substring(0, 2) == '01' || d.substring(0, 2) == '02' || d.substring(0, 2) == '03') {
        return new Date('2023-07-14 ' + d)
      } else {
        return new Date('2023-07-13 ' + d)
      }
    });

    const margins = { top: 100, right: 75, bottom: 50, left: 75 };
    const width = chartRef.current.parentElement.offsetWidth;
    const height = chartRef.current.parentElement.offsetHeight;
    const innerWidth = width - margins.left - margins.right;
    const innerHeight = height - margins.top - margins.bottom;

    //X and Y scale
    let xScale = d3.scaleLinear()
      .domain([d3.min(unique_sdt), d3.max(unique_sdt)])
      .range([0, innerWidth])

    let yScale = d3.scaleTime()
      .domain(d3.extent(parsedDateTimes))
      .range([innerHeight, 0])


    //Setting chart width and adjusting for margins
    const chart = d3.select(chartRef.current)
      .attr('width', innerWidth + margins.right + margins.left)
      .attr('height', height + margins.top + margins.bottom)
      .append('g')
      .attr('transform', 'translate(' + margins.left + ',' + margins.top + ')')
      .attr('id', 'chartid')


    // Chart background and outline
    chart.append("rect")
      .attr("width", width)
      .attr("height", height)
      .attr('transform', 'translate(' + -margins.left + ',' + -margins.top + ')')
      .attr("fill", "white")
      .attr("stroke", "#dee2e6")
      .attr("rx", 6)
      .attr("ry", 6)

    //Create tooltip
    const tooltip = d3.select('.container').append('div')
      .attr('class', 'tooltipotp')
      .html('Tooltip')
      .attr('id', 'tooltipid')


    // Format lines for planned series
    const line = d3.line()
      .x(function (d) {
        return xScale(d.shape_dist_traveled);
      })
      .y(function (d) {
        let realTime = new Date(d.real_stop_time * 1000).toISOString().substring(11, 18);
        let parsedDateTime;
        if (realTime.substring(0, 2) == '00' || realTime.substring(0, 2) == '01' || realTime.substring(0, 2) == '02' || realTime.substring(0, 2) == '03') {
          parsedDateTime = new Date('2023-07-14 ' + realTime);
        } else {
          parsedDateTime = new Date('2023-07-13 ' + realTime);
        }
        return yScale(parsedDateTime)
      })

    // Add lines for planned series
    chart.selectAll("myLines")
      .data(chartData)
      .join("path")
      .attr("d", d => line(d.values))
      .attr("stroke", (d) => {
        if (d.name == 'planned') {
          return 'black'
        } else { return 'none' }
      })
      .attr('transform', 'translate(10,' + 0 + ')')
      .style("stroke-width", 1)
      .style("fill", "none")

    // Add and format dots
    chart.selectAll('myDots')
      .data(chartData)
      .join('g')
      .style('fill', (d) => {
        if (d.name == 'planned') {
          return 'white'
        }
        else {
          return 'red'
        }
      })
      .style('stroke', (d) => {
        if (d.name == 'planned') {
          return 'black'
        } else { return 'none' }
      })
      .selectAll('myPoints')
      .data(d => d.values)
      .join('circle')
      .attr("cx", function (d) {
        return xScale(d.shape_dist_traveled);
      })
      .attr("cy", function (d) {
        let realTime = new Date(d.real_stop_time * 1000).toISOString().substring(11, 18);
        let parsedDateTime;
        if (realTime.substring(0, 2) == '00' || realTime.substring(0, 2) == '01' || realTime.substring(0, 2) == '02' || realTime.substring(0, 2) == '03') {
          parsedDateTime = new Date('2023-07-14 ' + realTime);
        } else {
          parsedDateTime = new Date('2023-07-13 ' + realTime);
        }
        return yScale(parsedDateTime)
      })
      .attr('fill', function (d) {
        if (d.timepoint==1 && d.date == "Planifié") {
          return 'black'
        };
      })
      .attr("r", 3)
      .attr('transform', 'translate(10,' + 0 + ')')
      .on('mouseover', d => {
        d3.select(d.target).attr("r", 10)
        chart.mouse = d3.pointer(d);
        chart.mouse2 = d3.pointer(d, d3.select(chart.chartLocation));
        tooltip.html('Date : ' + d.srcElement.__data__.date + '<br/>' +
          //since veh_id is not common to planned and real, display bugs when this attribute is added
          //t('tripotp.hover.vehicle') + d.srcElement.__data__.vehicle_id + '<br/>' +
          t('tripotp.hover.stop') + d.srcElement.__data__.stop_name + '<br/>' +
          t('tripotp.hover.time') + new Date(d.srcElement.__data__.real_stop_time * 1000).toISOString().substring(11, 19) 
          + '<br/>' + t('tripotp.hover.timepoint') + d.srcElement.__data__.timepoint)

          .style('left', chart.mouse2[0] - 75 + 'px')
          .style('top', chart.mouse2[1] - 150 + 'px')
          .style('opacity', .9);
      }).on('mouseout', (d) => {
        d3.select(d.target).attr("r", 3)
        tooltip.style('opacity', 0)
          .style('left', '0px');
      }).on("mouseleave", () => {
        tooltip.style('opacity', 0)
          .style('left', '0px');
      })

    //Append x axis
    chart.append('g')
      .attr('transform', 'translate(10,' + innerHeight + ')')
      .call(d3.axisBottom(xScale)
        .tickValues([xScale.domain()[0], xScale.domain()[1]])
        .tickFormat((d, i) => {
          if (width > 999) {
            return tickLabelsFormat[i]
          }
        })
      )
      .selectAll("text")
      .style("text-anchor", (d, i) => i === 0 ? "start" : "end") // Align the first tick to the start (left), and the last tick to the end (right)
      .attr("font-family", "Roboto")
      .style("font-size", "15px")
      .attr('class', 'x-axis')


    //Append y axis
    chart.append('g')
      .call(d3.axisLeft(yScale)
        .tickFormat(timeFormat)
        .ticks(d3.timeMinute.every(10))
      )
      .attr('class', 'yAxis')
      .attr("font-family", "Roboto")
      .style("font-size", "15px")
      .attr('class', 'y-axis')

    //Append chart title
    chart.append("text")
      .attr("x", (innerWidth / 2))
      .attr("y", 0 - (margins.top / 2))
      .attr("text-anchor", "middle")
      .attr("font-family", "Roboto")
      .style("font-size", "18px")
      .text(t('tripotp.route') + ' ' + tripRte + ' direction ' + (tripDir==in_label?t('tripotp.in'):t('tripotp.out')) + ' ' + t('tripotp.at') + ' ' + tripStart);


    function updateGraph() {

      //Redraw graph is window is resized
      d3.select("g").remove()
      setRefreshKey(oldKey => oldKey + 1);
    }

    d3.select(window).on('resize.updatesvg', updateGraph);

  }, [data, refreshKey]);


  return <svg ref={chartRef}></svg>;
};

export default ScatterChart;